import React, { useState, useEffect, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { 
  FaTrash, FaGripVertical, FaPlus, FaSave, 
  FaChevronLeft, FaFolder, FaTasks, FaRegCalendarAlt, FaSearch 
} from "react-icons/fa";
import { useStore } from "../../Components/StateProvider";
import { GetRecords, ErrorAlert, saveRecord, SuccessAlert , DeleteRecord} from "../../functions/api";
import Select from "react-select";

const TemplateMaster = () => {
  const store = useStore();
  
  const [view, setView] = useState("list"); 
  const [templates, setTemplates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);

  const [activeCategory, setActiveCategory] = useState("Non-Litigation"); 
  const [searchQuery, setSearchQuery] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: "", category: "Non-Litigation" });

  useEffect(() => {
    store.setmodule("dashboard");
    store.setheader("Template Master");
    initData();
  }, []);

  const initData = async () => {
    setLoading(true);
    await Promise.all([getTemplates(), getUsers()]);
    setLoading(false);
  };

  const getTemplates = async () => {
    const res = await GetRecords("/template/gettemplates");
    if (res.success) setTemplates(res.record || []);
  };

  const getUsers = async () => {
    const res = await GetRecords("/users/getusers?fieldname=all&sort=asc&pagesize=1000");
    if (res.success) setUsers(res.record?.list || []);
  };

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      const isTrademark = String(t.category_type) === "13";
      const isNonLitigation = String(t.category_type) !== "13";
      const categoryMatch = activeCategory === "Trademark" ? isTrademark : isNonLitigation;
      const searchMatch = t.template_name.toLowerCase().includes(searchQuery.toLowerCase());
      return categoryMatch && searchMatch;
    });
  }, [templates, activeCategory, searchQuery]);

  const openWorkflow = async (template) => {
    setActiveTemplate(template);
    setLoading(true);
    try {
      const res = await GetRecords(`/template/getworkflow/${template.id}`);
      // The backend returns wf.id, wf.task_name, etc.
      if (res.success && res.record) {
        setTasks(res.record.sort((a, b) => a.order_index - b.order_index));
      } else {
        setTasks([]);
      }
      setView("workflow");
    } catch (error) {
      ErrorAlert("Failed to load workflow");
    } finally {
      setLoading(false);
    }
  };

  // Ensure updateTask uses functional state updates
const updateTask = (index, field, value) => {
  setTasks(prevTasks => {
    const newTasks = [...prevTasks];
    newTasks[index] = { ...newTasks[index], [field]: value };
    return newTasks;
  });
};

const saveWorkflow = async () => {
  if (!activeTemplate?.id) return;
  
  setSaving(true);
  try {
    const payload = {
      template_id: Number(activeTemplate.id),
      created_by: Number(store.getuser().id),
      workflows: tasks.map((t, i) => ({
        // Ensure id is exactly what came from DB. New tasks must be 0.
        id: t.id && t.id > 1000000000 ? 0 : (t.id || 0), 
        task_name: String(t.task_name),
        assigned_to_user_id: t.assigned_to_user_id ? Number(t.assigned_to_user_id) : null,
        duration_days: Number(t.duration_days) || 0,
        priority: String(t.priority || "Low"),
        order_index: i + 1,
        status: String(t.status || "Active")
      }))
    };

    const res = await saveRecord("/template/saveworkflow", payload);
    if (res.success) {
      SuccessAlert("Saved Successfully");
      setView("list");
      getTemplates();
    }
  } catch (error) {
    ErrorAlert("Server Error: Check if all Task Names and Users are assigned.");
  } finally {
    setSaving(false);
  }
};
  const handleDeleteTemplate = async (templateId, e) => {
    // 1. Prevent the card from "opening" the workflow view
    if (e) e.stopPropagation();

    try {
      setLoading(true);
      
      // 2. Call your specific Delete helper
      // Based on your controller [HttpDelete("deletetemplate/{template_id}")]
      const res = await DeleteRecord(`/template/deletetemplate/${templateId}`);

      if (res.success) {
        // 3. Just refresh the list, the helper already showed the success alert
        getTemplates(); 
      }
    } catch (error) {
      console.error("Delete Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name || !newTemplate.category) return ErrorAlert("Fill all fields");
    setSaving(true);
    const res = await saveRecord("/template/createtemplate", {
      template_name: newTemplate.name,
      category_type: newTemplate.category === "Trademark" ? "13" : "Non-Litigation",
      created_by: store.getuser().id
    });
    if (res.success) {
      SuccessAlert("Template Created");
      setShowModal(false);
      getTemplates();
    }
    setSaving(false);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(tasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setTasks(items);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6">
      
      <div className="flex justify-between items-center mb-8">
        <div>
          {view === "workflow" ? (
            <button onClick={() => setView("list")} className="flex items-center gap-2 text-[#19186E] font-bold">
              <FaChevronLeft /> Back to Templates
            </button>
          ) : (
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Template Master</h1>
          )}
        </div>
        {view === "list" && (
          <button onClick={() => setShowModal(true)} className="bg-[#19186E] hover:bg-[#4338CA] text-[#F2F2F5] px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg active:scale-95 transition-all">
            <FaPlus /> Add Template
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#19186E]"></div></div>
      ) : view === "list" ? (
        <div className="space-y-6">
          <div className="flex gap-4 border-b border-slate-200">
            {["Non-Litigation", "Trademark"].map((tab) => (
              <button key={tab} onClick={() => setActiveCategory(tab)} className={`pb-4 px-2 text-sm font-bold relative ${activeCategory === tab ? "text-[#19186E]" : "text-slate-400"}`}>
                {tab}
                {activeCategory === tab && <div className="absolute bottom-0 left-0 w-full h-1 bg-[#19186E] rounded-t-full" />}
              </button>
            ))}
          </div>

          <div className="relative max-w-md">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" placeholder={`Search ${activeCategory}...`} className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none text-sm shadow-sm" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTemplates.map(t => (
              <div key={t.id} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all border-t-4 border-t-[#19186E]">
                <div className="flex justify-between mb-4">
                  <div className="p-3 bg-indigo-50 text-[#19186E] rounded-2xl"><FaFolder size={20} /></div>
                  <button
                  onClick={(e) => handleDeleteTemplate(t.id, e)}
                   className="text-slate-300 hover:text-red-500"><FaTrash size={14} /></button>
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-6 min-h-[56px] line-clamp-2">{t.template_name}</h3>
                <button onClick={() => openWorkflow(t)} className="w-full bg-[#F1F5F9] hover:bg-[#4338CA] text-[#19186E] py-3 rounded-xl text-sm font-bold  hover:text-white transition-all flex items-center justify-center gap-2">
                  <FaTasks size={14} /> Manage Workflow
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-8 py-4 bg-[#F1F5F9] text-[11px] font-bold uppercase tracking-wider text-[#64748B] items-center">
               <div className="col-span-1 text-center">#</div>
               <div className="col-span-3">Task Name</div>
               <div className="col-span-3">Assigned To</div>
               <div className="col-span-1 text-center">Days</div>
               <div className="col-span-2 text-center">Priority</div>
               <div className="col-span-2 text-center">Actions</div>
            </div>

            <div className="p-6">
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="workflow">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                      {tasks.map((task, index) => (
                        <Draggable key={String(task.id || index)} draggableId={String(task.id || index)} index={index}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} className={`grid grid-cols-12 gap-4 items-center p-4 bg-white border rounded-2xl transition-all ${snapshot.isDragging ? "shadow-2xl border-indigo-200 z-50 ring-2 ring-indigo-50" : "border-slate-100"}`}>
                              <div className="col-span-1 flex items-center justify-center gap-2">
                                <span {...provided.dragHandleProps} className="text-slate-300 hover:text-indigo-500 cursor-grab"><FaGripVertical /></span>
                                <span className="font-bold text-slate-400 text-sm">{index + 1}</span>
                              </div>
                              <div className="col-span-3">
                                <input className="w-full bg-transparent border-b border-transparent focus:border-[#19186E] py-1 outline-none font-medium text-slate-700" value={task.task_name} onChange={(e) => updateTask(index, "task_name", e.target.value)} />
                              </div>
                              <div className="col-span-3 px-2">
                                <Select 
                                  options={users.map(u => ({ value: u.id, label: u.userName || `${u.FirstName} ${u.LastName}` }))}
                                  value={users.filter(u => u.id === task.assigned_to_user_id).map(u => ({ value: u.id, label: u.userName || `${u.FirstName} ${u.LastName}` }))[0] || null}
                                  onChange={(opt) => updateTask(index, "assigned_to_user_id", opt?.value)}
                                  styles={customSelectStyles}
                                />
                              </div>
                              <div className="col-span-1 flex justify-center">
                                <input type="number" className="w-12 bg-[#F8FAFC] border border-slate-100 rounded-lg text-center font-bold text-[#19186E] py-1.5" value={task.duration_days} onChange={(e) => updateTask(index, "duration_days", e.target.value)} />
                              </div>
                              <div className="col-span-2 px-2">
                                <select 
                                  value={task.priority} 
                                  onChange={(e) => updateTask(index, "priority", e.target.value)}
                                  className="w-full font-bold text-[10px] uppercase rounded-full px-3 py-2 border border-slate-100 outline-none cursor-pointer"
                                  style={{ color: task.priority === "High" ? "#dc2626" : task.priority === "Medium" ? "#f59e0b" : "#10b981" }}
                                >
                                  <option value="Low">Low</option>
                                  <option value="Medium">Medium</option>
                                  <option value="High">High</option>
                                </select>
                              </div>
                              <div className="col-span-2 text-center">
                                <button onClick={() => setTasks(tasks.filter((_, i) => i !== index))} className="p-2 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50"><FaTrash size={14} /></button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <button onClick={() => setTasks([...tasks, { id: 0, task_name: "", duration_days: 1, priority: "Low", status: "Active" }])} className="mt-8 flex items-center gap-2 text-[#4F46E5] font-bold text-sm hover:translate-x-1 transition-transform px-4">
                <FaPlus /> ADD NEW TASK
              </button>
            </div>
            <div className="p-8 bg-slate-50 border-t border-slate-100 flex justify-end">
               <button onClick={saveWorkflow} disabled={saving} className=" bg-[#19186E] hover:bg-[#4338CA] text-[#F2F2F5] px-12 py-4 rounded-2xl font-bold shadow-lg hover:bg-[#2d2c8a] transition-all disabled:opacity-50">
                 <FaSave className="inline mr-2" /> {saving ? "Saving..." : "Save Template Workflow"}
               </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL */}
      {showModal && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
  >
    <div
      className="rounded-xl shadow-2xl w-full relative"
      style={{
        maxWidth: "500px",
        backgroundColor: "#FFFFFF",
        padding: "24px",
      }}
    >
      {/* Close Icon */}
      <button
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        onClick={() => setShowModal(false)}
      >
        <FaPlus style={{ transform: 'rotate(45deg)' }} size={18} />
      </button>

      <h2
        className="text-xl font-bold mb-6 text-center"
        style={{ color: "#111827" }}
      >
        New Template
      </h2>

      <div className="space-y-4">
        {/* Category Field */}
        <div>
          <label
            className="block text-sm font-medium mb-1"
            style={{ color: "#374151" }}
          >
            Category
          </label>
          <select
            className="w-full p-2.5 rounded-md border outline-none transition-all focus:ring-2 focus:ring-blue-50 focus:border-blue-500"
            style={{
              borderColor: "#D1D5DB",
              backgroundColor: "#F9FAFB",
              color: "#1F2937",
              fontSize: "14px"
            }}
            onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
            value={newTemplate.category}
          >
            <option value="Non-Litigation">Non-Litigation</option>
            <option value="Trademark">Trademark</option>
          </select>
        </div>

        {/* Template Name Field */}
        <div>
          <label
            className="block text-sm font-medium mb-1"
            style={{ color: "#374151" }}
          >
            Template Name
          </label>
          <input
            type="text"
            placeholder="Enter template name..."
            className="w-full p-2.5 rounded-md border outline-none transition-all focus:ring-2 focus:ring-blue-50 focus:border-blue-500"
            style={{
              borderColor: "#D1D5DB",
              backgroundColor: "#F9FAFB",
              color: "#1F2937",
              fontSize: "14px"
            }}
            value={newTemplate.name}
            onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-8">
          <button
            onClick={() => setShowModal(false)}
            className="px-5 py-2.5 rounded-md font-semibold transition-colors duration-200"
            style={{
              backgroundColor: "#E5E7EB",
              color: "#374151",
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleCreateTemplate}
            className="px-6 py-2.5 rounded-md font-semibold transition-all duration-200 hover:opacity-90 active:scale-95"
            style={{
              backgroundColor: "#2563EB",
              color: "#FFFFFF",
            }}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

const customSelectStyles = {
  control: (base) => ({ 
    ...base, 
    border: "1px solid #E2E8F0", 
    backgroundColor: "#F8FAFC", 
    borderRadius: "10px", 
    fontSize: "11px", 
    fontWeight: "600",
    minHeight: "38px"
  }),
  menu: (provided) => ({ ...provided, zIndex: 9999 })
};

export default TemplateMaster;